/**
 * Agent payment amount safeguard (#36683). On the mutual brands an agent (any staff user without
 * CanManuallyChargePolicy) never types a payment amount: they pick one of these presets, built from
 * the policy's billing schedule. staff-fe builds the choices from the billing details it shows, and
 * the staff-billing-details lambda rebuilds them from the live schedule and refuses a charge whose
 * amount is not the chosen preset, so a typo like $1,000 for $100 can't reach Stripe even through
 * the API.
 *
 *  - Amount due now: the next scheduled payment once its date has arrived, which is also where an
 *    overdue installment sits after an NSF, or one the member was already billed for whatever its
 *    charge date: a migrated CFM term's GO open item, which a migration billing hold only delays
 *    Branch collecting (#37417). Endorsement premium on the mutuals is spread into the
 *    remaining installments, so it is already inside this amount; there is no separate option.
 *  - Next installment: the next scheduled payment before it is due. Paying it is Pay Ahead: that
 *    installment is not charged or invoiced again on its date.
 *  - Remaining balance: everything left on the term.
 *
 * When the amount due is the whole remaining balance only "amount due now" is offered, and a next
 * installment that is the whole balance is offered as the remaining balance, so a pay-in-full
 * policy shows one choice. No preset ever exceeds the remaining balance.
 */

export const AgentPaymentOption = {
  AmountDue: 'AMOUNT_DUE',
  NextInstallment: 'NEXT_INSTALLMENT',
  RemainingBalance: 'REMAINING_BALANCE'
} as const;

export type AgentPaymentOptionValue = (typeof AgentPaymentOption)[keyof typeof AgentPaymentOption];

export type AgentPaymentPreset = {
  option: AgentPaymentOptionValue;
  amount: number;
  /** The installment's scheduled date (yyyy-MM-dd); absent on the remaining balance. */
  dueDate?: string;
};

const toCents = (amount: number) => Math.round(amount * 100);

export function getAgentPaymentPresets({
  nextPaymentDate,
  nextPaymentAmount,
  nextPaymentBilled = false,
  nextPaymentDueDate,
  payAheadAllowed = true,
  totalRemaining,
  today
}: {
  /** The next scheduled payment's date, yyyy-MM-dd (billing details `nextPaymentDate`). */
  nextPaymentDate: string | null | undefined;
  /** What that payment charges, card fee included where one applies (`nextPaymentTotal ?? nextPaymentAmount`). */
  nextPaymentAmount: number | null | undefined;
  /** The member was already billed for that payment (billing details `nextPaymentBilled`, #37417). */
  nextPaymentBilled?: boolean | null;
  /**
   * The payment's own due date when it differs from the date it is charged (billing details
   * `nextPaymentDueDate`): GO's due date for an open item a migration hold delays (#37421). Labels the
   * amount due now; defaults to `nextPaymentDate`.
   */
  nextPaymentDueDate?: string | null;
  /**
   * False while a billing hold is active on a term that isn't billed on GO's schedule: paying the next
   * installment early writes a Pay Ahead hold, which would overwrite the hold in place (#37421). A
   * direct-bill installment plan stays true: its payment is stamped with the installment and writes
   * no hold.
   */
  payAheadAllowed?: boolean;
  totalRemaining: number | null | undefined;
  /** Business today in the policy's state, yyyy-MM-dd (getTodayStr(policy.state)). */
  today: string;
}): AgentPaymentPreset[] {
  const remainingCents = toCents(Number(totalRemaining) || 0);
  if (remainingCents <= 0) {
    return [];
  }

  const presets: AgentPaymentPreset[] = [];
  const nextCents = Math.min(toCents(Number(nextPaymentAmount) || 0), remainingCents);
  if (nextPaymentDate && nextCents > 0) {
    if (nextPaymentDate <= today || nextPaymentBilled) {
      presets.push({
        option: AgentPaymentOption.AmountDue,
        amount: nextCents / 100,
        dueDate: nextPaymentDueDate || nextPaymentDate
      });
    } else if (nextCents < remainingCents && payAheadAllowed) {
      // A last installment that is the whole balance reads as the remaining balance: nothing to pay ahead of.
      presets.push({ option: AgentPaymentOption.NextInstallment, amount: nextCents / 100, dueDate: nextPaymentDate });
    }
  }
  if (!presets.some((preset) => toCents(preset.amount) === remainingCents)) {
    presets.push({ option: AgentPaymentOption.RemainingBalance, amount: remainingCents / 100 });
  }
  return presets;
}

/** The preset for `option`, provided `amount` is that preset's amount to the cent; otherwise undefined. */
export function findAgentPaymentPreset(
  presets: AgentPaymentPreset[],
  option: string | null | undefined,
  amount: number
): AgentPaymentPreset | undefined {
  return presets.find((preset) => preset.option === option && toCents(preset.amount) === toCents(amount));
}
